import { useCallback, useMemo } from 'react';
import type { CommentMention } from '../../containers/CommentList/CommentList.types';
import { MENTION_REGEX, URL_REGEX } from './parsingConstants';
import { extractDomain, isValidUrl, normalizeUrl } from './urlParsing';
// keep re-exports for external consumers; internal uses are via local functions
import './mentionParsing';
import { extractHyperlinks as extractHyperlinksImpl } from './textParser';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface MentionMatch {
  username: string;
  email: string;
  name: string;
  startIndex: number;
  endIndex: number;
  mentionText: string;
}

export interface HyperlinkMatch {
  url: string;
  domain: string;
  startIndex: number;
  endIndex: number;
  displayText: string;
}

export interface TextPart {
  text: string;
  type: 'text' | 'mention' | 'hyperlink';
  username?: string;
  email?: string;
  name?: string;
  url?: string;
  domain?: string;
}

// ============================================================================
// CONSTANTS & REGEX PATTERNS
// ============================================================================

// Regex now imported from parsingConstants

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// URL helpers are imported; remove local duplicates

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Unified hook for parsing text with mentions and hyperlinks
 * Replaces useHyperlinks, useCommentTextParsing, and scattered parsing logic
 */
export function useTextParsing() {
  /**
   * Find positions of known mentions in text
   * Use this when you already have the mention data and just need positions
   */
  const findMentionPositions = useCallback(
    (text: string, commentMentions: CommentMention[]): MentionMatch[] => {
      const extractedMentions: MentionMatch[] = [];

      // First, find all unique mention positions in the text using regex
      const allMentionsInText = new Set<string>();
      const mentionPositions: Array<{
        username: string;
        startIndex: number;
        endIndex: number;
      }> = [];

      // Reset regex state
      MENTION_REGEX.lastIndex = 0;

      let match;
      while ((match = MENTION_REGEX.exec(text)) !== null) {
        const username = match[1];
        const mentionKey = `${username}:${match.index}`;

        // Only add if we haven't seen this exact position before
        if (!allMentionsInText.has(mentionKey)) {
          allMentionsInText.add(mentionKey);
          mentionPositions.push({
            username,
            startIndex: match.index,
            endIndex: match.index + match[0].length
          });
        }
      }

      // Now match each found mention with the commentMentions data
      mentionPositions.forEach(mentionPos => {
        const commentMention = commentMentions.find(cm => {
          const cmUsername = cm.uid.startsWith('@') ? cm.uid.slice(1) : cm.uid;
          return cmUsername === mentionPos.username;
        });

        if (commentMention) {
          extractedMentions.push({
            username: mentionPos.username,
            email: commentMention.email,
            name: commentMention.fullName,
            startIndex: mentionPos.startIndex,
            endIndex: mentionPos.endIndex,
            mentionText: text.substring(
              mentionPos.startIndex,
              mentionPos.endIndex
            )
          });
        }
      });

      // Sort by position in text
      return extractedMentions.sort((a, b) => a.startIndex - b.startIndex);
    },
    []
  );

  /**
   * Discover all @mentions in text using regex
   * Use this when you need to find mentions without prior knowledge
   * Returns only basic mention info (you'll need to lookup user details separately)
   */
  const discoverMentions = useCallback(
    (
      text: string
    ): Array<{
      username: string;
      startIndex: number;
      endIndex: number;
      mentionText: string;
    }> => {
      const mentions: Array<{
        username: string;
        startIndex: number;
        endIndex: number;
        mentionText: string;
      }> = [];

      // Reset regex state
      MENTION_REGEX.lastIndex = 0;

      let match;
      while ((match = MENTION_REGEX.exec(text)) !== null) {
        mentions.push({
          username: match[1],
          startIndex: match.index,
          endIndex: match.index + match[0].length,
          mentionText: match[0]
        });
      }

      return mentions;
    },
    []
  );

  /**
   * Main extraction function - uses known mentions if provided, otherwise discovers them
   */
  const extractMentions = useCallback(
    (text: string, commentMentions?: CommentMention[]): MentionMatch[] => {
      if (!text) {
        return [];
      }

      // If we have known mentions, use them directly
      if (commentMentions && commentMentions.length > 0) {
        return findMentionPositions(text, commentMentions);
      }

      // Otherwise, we can only discover mention patterns (without user data)
      // This case might not be useful for display, but could be for analysis
      return discoverMentions(text).map(mention => ({
        ...mention,
        email: '', // No user data available
        name: mention.username // Fallback to username
      }));
    },
    [findMentionPositions, discoverMentions]
  );

  /**
   * Extract hyperlinks from text
   * This is the centralized hyperlink extraction logic
   */
  const extractHyperlinks = useCallback(
    (text: string): HyperlinkMatch[] => extractHyperlinksImpl(text),
    []
  );

  /**
   * Check if a user is mentioned in text by username
   * Used by useMostRecentMention and similar functionality
   */
  const isUserMentioned = useCallback(
    (text: string, username: string): boolean => {
      const mentionPattern = new RegExp(`@${username}\\b`, 'i');
      return mentionPattern.test(text);
    },
    []
  );

  /**
   * Parse text and split into parts with mentions and hyperlinks
   * This is the main parsing function that replaces all scattered parsing logic
   */
  const parseText = useCallback(
    (text: string, commentMentions?: CommentMention[]): TextPart[] => {
      if (!text) {
        return [];
      }

      const parts: TextPart[] = [];

      // Extract mentions and hyperlinks
      const mentions = extractMentions(text, commentMentions);
      const hyperlinks = extractHyperlinks(text);

      // Combine all matches and sort by position
      const allMatches: Array<
        | { type: 'mention'; match: MentionMatch }
        | { type: 'hyperlink'; match: HyperlinkMatch }
      > = [
        ...mentions.map(match => ({ type: 'mention' as const, match })),
        ...hyperlinks.map(match => ({ type: 'hyperlink' as const, match }))
      ].sort((a, b) => a.match.startIndex - b.match.startIndex);

      let lastIndex = 0;

      // Process each match
      allMatches.forEach(item => {
        const match = item.match;

        // Add text before this match
        if (match.startIndex > lastIndex) {
          const beforeText = text.substring(lastIndex, match.startIndex);
          if (beforeText) {
            parts.push({
              text: beforeText,
              type: 'text'
            });
          }
        }

        // Add the match
        if (item.type === 'mention') {
          const mentionMatch = match as MentionMatch;
          parts.push({
            text: mentionMatch.mentionText,
            type: 'mention',
            username: mentionMatch.username,
            email: mentionMatch.email,
            name: mentionMatch.name
          });
        } else {
          const hyperlinkMatch = match as HyperlinkMatch;
          parts.push({
            text: hyperlinkMatch.displayText,
            type: 'hyperlink',
            url: hyperlinkMatch.url,
            domain: hyperlinkMatch.domain
          });
        }

        lastIndex = match.endIndex;
      });

      // Add remaining text after last match
      if (lastIndex < text.length) {
        const afterText = text.substring(lastIndex);
        if (afterText) {
          parts.push({
            text: afterText,
            type: 'text'
          });
        }
      }

      return parts;
    },
    [extractMentions, extractHyperlinks]
  );

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Main parsing functions
      parseText,
      extractMentions,
      extractHyperlinks: extractHyperlinks,

      // Specific mention functions
      findMentionPositions,
      discoverMentions,

      // Utility functions
      isUserMentioned,
      extractDomain,
      normalizeUrl,
      isValidUrl,

      // Constants (for external use if needed)
      MENTION_REGEX,
      URL_REGEX
    }),
    [
      parseText,
      extractMentions,
      extractHyperlinks,
      findMentionPositions,
      discoverMentions,
      isUserMentioned
    ]
  );
}
